import { Room, RoomEvent } from 'livekit-client';

const VOICE_SESSION_PATH = '/conductor/voice_sessions';
const TRANSCRIPT_TOPIC = 'conductor.transcript';
const MICROPHONE_CAPTURE_OPTIONS = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true
};

const voiceSessionHeaders = (csrfToken) => ({
  'Content-Type': 'application/json',
  'X-CSRF-Token': csrfToken,
  Accept: 'application/json'
});

const readableVoiceError = (error) => {
  const message = error?.message || '';
  const name = error?.name || '';

  if (/permission|denied|notallowed/i.test(`${name} ${message}`)) {
    return new Error('Microphone access was blocked.');
  }

  if (message) {
    return new Error(message);
  }

  return new Error('Unable to start voice mode.');
};

const attachRemoteAudioTrack = (track, audioElements) => {
  if (!track || track.kind !== 'audio' || typeof track.attach !== 'function') {
    return;
  }

  const element = track.attach();
  if (!element) return;

  element.autoplay = true;
  element.playsInline = true;
  element.setAttribute('data-conductor-voice-audio', 'true');
  element.style.display = 'none';
  document.body.appendChild(element);
  audioElements.push({ track, element });
};

const detachRemoteAudioTrack = (track, audioElements) => {
  const remainingElements = [];

  audioElements.forEach((entry) => {
    if (entry.track !== track) {
      remainingElements.push(entry);
      return;
    }

    if (typeof entry.track.detach === 'function') {
      entry.track.detach(entry.element);
    }

    entry.element.remove();
  });

  audioElements.splice(0, audioElements.length, ...remainingElements);
};

const decodeDataPayload = (payload) => {
  if (typeof payload === 'string') return payload;
  if (payload instanceof ArrayBuffer) {
    const bytes = new Uint8Array(payload);
    if (typeof TextDecoder !== 'undefined') return new TextDecoder().decode(bytes);
    return String.fromCharCode(...bytes);
  }
  if (ArrayBuffer.isView(payload)) {
    if (typeof TextDecoder !== 'undefined') return new TextDecoder().decode(payload);
    return String.fromCharCode(...payload);
  }

  return '';
};

const parseVoiceUpdate = (payload, topic) => {
  if (topic && topic !== TRANSCRIPT_TOPIC) return null;

  try {
    const data = JSON.parse(decodeDataPayload(payload));
    if (data?.type !== 'transcript' && data?.type !== 'voice_activity') return null;

    return data;
  } catch (e) {
    return null;
  }
};

export const createVoiceSession = async ({ threadId, csrfToken, conductorInterface = null }) => {
  const response = await fetch(VOICE_SESSION_PATH, {
    method: 'POST',
    headers: voiceSessionHeaders(csrfToken),
    body: JSON.stringify({ thread_id: threadId, interface: conductorInterface })
  });

  if (!response.ok) {
    throw new Error('Unable to create voice session.');
  }

  return response.json();
};

export const startVoiceSession = async ({
  threadId,
  csrfToken,
  conductorInterface = null,
  onDisconnected = null,
  onConnectionStateChanged = null,
  onTranscript = null
}) => {
  const credentials = await createVoiceSession({ threadId, csrfToken, conductorInterface });
  const room = new Room();
  const remoteAudioElements = [];

  if (onDisconnected) {
    room.on(RoomEvent.Disconnected, onDisconnected);
  }

  if (onConnectionStateChanged) {
    room.on(RoomEvent.ConnectionStateChanged, onConnectionStateChanged);
  }

  room.on(RoomEvent.TrackSubscribed, (track) => {
    attachRemoteAudioTrack(track, remoteAudioElements);
  });
  room.on(RoomEvent.TrackUnsubscribed, (track) => {
    detachRemoteAudioTrack(track, remoteAudioElements);
  });
  room.on(RoomEvent.DataReceived, (payload, _participant, _kind, topic) => {
    if (!onTranscript) return;

    const voiceUpdate = parseVoiceUpdate(payload, topic);
    if (voiceUpdate) {
      onTranscript(voiceUpdate);
    }
  });

  try {
    await room.connect(credentials.server_url, credentials.token);
    await room.localParticipant.setMicrophoneEnabled(true, MICROPHONE_CAPTURE_OPTIONS);
  } catch (error) {
    room.disconnect();
    throw readableVoiceError(error);
  }

  return {
    room,
    credentials,
    remoteAudioElements
  };
};

export const stopVoiceSession = async (voiceSession) => {
  if (voiceSession?.remoteAudioElements) {
    [...voiceSession.remoteAudioElements].forEach(({ track }) => {
      detachRemoteAudioTrack(track, voiceSession.remoteAudioElements);
    });
  }

  if (voiceSession?.room) {
    voiceSession.room.disconnect();
  }
};

export const setVoiceSessionMicrophoneEnabled = async (voiceSession, enabled) => {
  const localParticipant = voiceSession?.room?.localParticipant;
  if (!localParticipant || typeof localParticipant.setMicrophoneEnabled !== 'function') {
    return;
  }

  await localParticipant.setMicrophoneEnabled(
    enabled,
    enabled ? MICROPHONE_CAPTURE_OPTIONS : undefined
  );
};
